import { AfterViewInit, Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit, ViewChild, inject } from '@angular/core'
import { Router, RouterLink, RouterLinkActive } from '@angular/router'
import { Subscription } from 'rxjs'
import { DxDropDownButtonModule } from 'devextreme-angular/ui/drop-down-button'
import { ThomasWidgetComponent } from '../../thomas/thomas-widget.component'
import { ThomasConversationService } from '../../thomas/thomas-conversation.service'
import { UserDto } from 'src/app/types/dto/user.dto'
import { CompanyDto } from 'src/app/types/dto/company.dto'
import { AuthService } from 'src/app/shared/services/auth.service'
import { NavigationService } from '../../services/navigation.service'
import { navigation } from '../../../app-navigation'
import { environment } from '../../../../environments/environment'

interface AppSwitcherItem {
  text: string
  icon: string
  onClick: () => void
}

interface NavTab {
  text: string
  path: string
}

@Component({
    selector: 'app-header',
    templateUrl: 'header.component.html',
    styleUrls: ['./header.component.scss'],
    imports: [DxDropDownButtonModule, RouterLink, RouterLinkActive, ThomasWidgetComponent]
})
export class HeaderComponent implements OnInit, AfterViewInit, OnDestroy {
  private static readonly NAV_COLLAPSE_COMFORT_PX = 32

  private auth = inject(AuthService);
  private router = inject(Router);
  private navigationService = inject(NavigationService);
  private thomas = inject(ThomasConversationService);
  private zone = inject(NgZone);

  title = 'Hub'
  isUAT = false
  innerWidth = window.innerWidth

  user: UserDto | undefined
  company: CompanyDto | undefined

  navCollapsed = false
  collapsedMenuItems: any[] = []
  private navExpandedWidth = 0
  private subscriptions: Subscription[] = []

  @ViewChild('innerNav') private innerNavRef?: ElementRef<HTMLElement>

  // ResizeObserver runs outside Angular's zone
  private navResizeObserver = typeof ResizeObserver === 'undefined' ? undefined
    : new ResizeObserver(() => this.zone.run(() => this.checkNavFit()))

  // top tab links - Dashboards only
  navTabs: NavTab[] = navigation
    .filter((item: any) => item.path && item.text !== 'Home')
    .map((item: any) => ({
      text: item.text,
      path: /^\//.test(item.path) ? item.path : `/${item.path}`,
    }))

  dropdownItems: AppSwitcherItem[] = []

  get signedIn(): boolean {
    return !!this.user?.id
  }

  get activeTab(): NavTab | undefined {
    const segment = this.router.url.split(/[?#]/)[0].split('/')[1]
    return this.navTabs.find(tab => tab.path === `/${segment}`)
  }

  get activeTabText(): string {
    return this.activeTab?.text ?? 'Menu'
  }

  ngOnInit() {
    this.isUAT = environment.applicationUrl.startsWith('https://uat-');
    this.subscriptions.push(this.auth.currentUser.subscribe((user) => {
      this.user = user
      this.scheduleNavFitCheck()
    }))
    this.subscriptions.push(this.auth.currentCompany.subscribe((company) => {
      this.company = company
      this.scheduleNavFitCheck()
    }))

    this.setupDropDownItems()
    // permissions arrive async and flip the visible flags on the app list
    this.subscriptions.push(this.navigationService.navigationUpdated.subscribe(() => this.setupDropDownItems()))
  }

  ngAfterViewInit() {
    if (this.innerNavRef) {
      this.navResizeObserver?.observe(this.innerNavRef.nativeElement)
    }
  }

  ngOnDestroy() {
    this.navResizeObserver?.disconnect()
    this.subscriptions.forEach(sub => sub.unsubscribe())
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.innerWidth = window.innerWidth
  }

  private checkNavFit() {
    const navEl = this.innerNavRef?.nativeElement
    if (!navEl) {
      return
    }
    let shouldCollapse: boolean
    if (this.navCollapsed) {
      shouldCollapse = this.navExpandedWidth + HeaderComponent.NAV_COLLAPSE_COMFORT_PX > navEl.clientWidth
    } else {
      shouldCollapse = navEl.scrollWidth > navEl.clientWidth
      if (shouldCollapse) {
        this.navExpandedWidth = navEl.scrollWidth
      }
    }
    if (shouldCollapse !== this.navCollapsed) {
      this.navCollapsed = shouldCollapse
      if (!shouldCollapse) {
        this.scheduleNavFitCheck()
      }
    }
  }

  private scheduleNavFitCheck() {
    requestAnimationFrame(() => this.checkNavFit())
  }

  buildNavMenuItems() {
    const items: any[] = this.navTabs.map(tab => ({ ...tab }))
    if (this.thomas.available) {
      items.push({ text: 'Ask AI', isAskAi: true })
    }
    if (this.company) {
      items.push({ text: this.company.companyShortName, isCompany: true })
    }
    this.collapsedMenuItems = items
  }

  onCollapsedItemClick(e: any) {
    const item = e.itemData
    if (item.isAskAi) {
      this.thomas.toggle()
    } else if (item.isCompany) {
      this.goHome()
    } else if (item.path) {
      this.router.navigate([item.path])
    }
  }

  setupDropDownItems() {
    const apps: any[] = navigation.find((item) => item.text === 'Applications')?.items ?? []

    this.dropdownItems = apps
      .filter((app) => app.visible !== false)
      .map((app) => ({
        text: app.text,
        icon: app.icon,
        onClick: () => this.launchApp(app.link),
      }))
  }

  launchApp(link: string) {
    const envField = link as keyof typeof environment
    const url = environment[envField] as string
    if (url) {
      window.open(url, '_blank')
    }
  }

  goHome() {
    this.router.navigate(['/home'])
  }
}
